import { parseAiResult, parseReceipt, parseRecipeDraft, resolveAiResult, versteheLokal, type Receipt, type RecipeDraft, type ResolvedResult } from "@ainkauf/core";
import { db } from "@/db/dexie";
import { allLists, allProducts } from "@/db/repository";
import { activeFamilyId } from "@/lib/session";
import { config } from "@/lib/config";
import { getSupabase } from "@/lib/supabase";
import { CATEGORIES } from "@ainkauf/core";

/**
 * Der Weg von gesprochener Sprache zu Aktionen auf Listen.
 *
 * Der Kontext kommt aus Dexie, nicht vom Server: die App weiß selbst, welche
 * Listen und welche Artikel die Familie hat, und spart damit eine Abfrage und
 * eine Menge Wartezeit.
 */
async function zugriffstoken(): Promise<string | null> {
  const client = getSupabase();
  if (!client) return null;
  const { data } = await client.auth.getSession();
  return data.session?.access_token ?? null;
}

function basis(): string {
  return `${config().supabaseUrl}/functions/v1`;
}

export async function transkribiere(audio: Blob): Promise<string> {
  const token = await zugriffstoken();
  if (token === null) throw new Error("Nicht angemeldet");

  const listen = (await allLists()).map((liste) => liste.name);
  const produkte = (await allProducts()).sort((a, b) => b.use_count - a.use_count).map((p) => p.name);

  const form = new FormData();
  form.append("audio", audio, "aufnahme.webm");
  form.append("listen", JSON.stringify(listen));
  form.append("produkte", JSON.stringify(produkte.slice(0, 50)));
  form.append("family_id", activeFamilyId());

  const antwort = await fetch(`${basis()}/transcribe`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });

  const daten = (await antwort.json()) as { text?: string; error?: string };
  if (!antwort.ok) throw new Error(daten.error ?? `Fehler ${String(antwort.status)}`);
  return daten.text ?? "";
}

/** Text oder Foto zu Aktionen. Das Ergebnis ist ein Vorschlag, kein Befehl. */
export async function verstehe(eingabe: { text?: string; image?: string; currentListId?: string | null }): Promise<ResolvedResult> {
  const token = await zugriffstoken();
  if (token === null) throw new Error("Nicht angemeldet");

  const listen = await allLists();
  const produkte = await allProducts();

  /*
    Der häufigste Satz im Laden ist eine Aufzählung: "Milch, Butter und sechs
    Eier". Dafür reicht der Parser, den die App für die Tastatur ohnehin hat,
    und der Weg über das Modell entfällt samt seiner Sekunden. Erkennt der
    Kern die Eingabe nicht eindeutig als Aufzählung, sagt er nein und es geht
    weiter wie bisher. Bilder gehen immer an das Modell.
  */
  if (eingabe.text && !eingabe.image) {
    const lokal = versteheLokal(eingabe.text, eingabe.currentListId ?? null);
    if (lokal) {
      return resolveAiResult(lokal, { lists: listen, products: produkte, currentListId: eingabe.currentListId ?? null });
    }
  }

  // Sechzig statt zweihundert Produkte: Der Katalog wächst mit den Jahren, die
  // Wartezeit soll es nicht. Was hier fehlt, findet der Kern hinterher über
  // den normalisierten Namen ohnehin.
  const haeufige = [...produkte].sort((a, b) => b.use_count - a.use_count).slice(0, 60);
  const laeden = await db.stores.where("family_id").equals(activeFamilyId()).toArray();

  const antwort = await fetch(`${basis()}/assistant`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({
      text: eingabe.text,
      image: eingabe.image,
      family_id: activeFamilyId(),
      kontext: {
        listen: listen.map((liste) => ({
          id: liste.id,
          name: liste.name,
          laden: laeden.find((laden) => laden.id === liste.store_id)?.name ?? null,
        })),
        produkte: haeufige.map((p) => ({ id: p.id, name: p.name, kategorie: p.category, einheit: p.default_unit })),
        kategorien: CATEGORIES.map((eintrag) => ({ key: eintrag.key, label: eintrag.label })),
        offeneListe: eingabe.currentListId ? (listen.find((l) => l.id === eingabe.currentListId)?.name ?? null) : null,
        heute: new Date().toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long", year: "numeric" }),
      },
    }),
  });

  const roh = (await antwort.json()) as unknown;
  if (!antwort.ok) {
    const fehler = (roh as { error?: string }).error;
    throw new Error(fehler ?? `Fehler ${String(antwort.status)}`);
  }

  const geparst = parseAiResult(roh);
  if (!geparst.ok) throw new Error(`Unerwartete Antwort: ${geparst.error}`);

  return resolveAiResult(geparst.value, {
    lists: listen,
    products: produkte,
    currentListId: eingabe.currentListId ?? null,
  });
}

/**
 * Ein Foto eines Kassenbons zu Laden, Datum, Summe und Positionen.
 *
 * Zugeordnet und gespeichert wird hier nichts. Die Funktion liefert, was auf
 * dem Bon steht, und die App entscheidet, was davon zu welchem Artikel gehört
 * (siehe planReceipt im Kern). So bleibt der teure Teil einmalig und der
 * Abgleich prüfbar, ohne ein Modell zu fragen.
 */
export async function leseBon(image: string): Promise<Receipt> {
  const token = await zugriffstoken();
  if (token === null) throw new Error("Nicht angemeldet");

  const antwort = await fetch(`${basis()}/parse-receipt`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ image, family_id: activeFamilyId() }),
  });

  const roh = (await antwort.json()) as unknown;
  if (!antwort.ok) throw new Error((roh as { error?: string }).error ?? `Fehler ${String(antwort.status)}`);

  const geparst = parseReceipt(roh);
  if (!geparst.ok) throw new Error(`Unerwartete Antwort: ${geparst.error}`);
  return geparst.value;
}

/**
 * Ein Rezept aus Text, Link oder Foto.
 *
 * Der Link wird nicht hier geholt, sondern in der Edge Function: Der Browser
 * dürfte fremde Seiten wegen CORS gar nicht lesen, und ein Rezeptserver soll
 * auch nicht sehen, wer gerade kocht.
 */
export async function leseRezept(eingabe: { text?: string; url?: string; image?: string }): Promise<RecipeDraft> {
  const token = await zugriffstoken();
  if (token === null) throw new Error("Nicht angemeldet");

  const antwort = await fetch(`${basis()}/parse-recipe`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({
      ...eingabe,
      kategorien: CATEGORIES.map((k) => ({ key: k.key, label: k.label })),
      family_id: activeFamilyId(),
    }),
  });

  const roh = (await antwort.json()) as unknown;
  if (!antwort.ok) throw new Error((roh as { error?: string }).error ?? `Fehler ${String(antwort.status)}`);

  const geparst = parseRecipeDraft(roh);
  if (!geparst.ok) throw new Error(`Unerwartete Antwort: ${geparst.error}`);
  return geparst.value;
}
